import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Box,
  Image,
  Text,
  Button,
  VStack,
  HStack,
  IconButton,
  useDisclosure,
  Link,
} from '@chakra-ui/react';
import { HiArrowLeft, HiArrowRight } from 'react-icons/hi';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { API } from '../../constants';

// Function to detect URLs and make them clickable
const linkifyText = (text) => {
  if (!text) return text;
  
  // URL regex pattern - matches http, https, www, and common domains
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9-]+\.[a-zA-Z]{2,}[^\s]*)/g;
  
  const parts = [];
  let lastIndex = 0;
  let match;
  
  // Reset regex lastIndex
  urlRegex.lastIndex = 0;
  
  while ((match = urlRegex.exec(text)) !== null) {
    // Add text before the URL
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex, match.index)
      });
    }
    
    // Add the URL
    let url = match[0];
    let displayUrl = match[0];
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }
    
    parts.push({
      type: 'link',
      url: url,
      display: displayUrl
    });
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.substring(lastIndex)
    });
  }
  
  // If no URLs found, return original text
  if (parts.length === 0) {
    return text;
  }
  
  return parts.map((part, index) => {
    if (part.type === 'link') {
      return (
        <Link
          key={index}
          href={part.url}
          target="_blank"
          rel="noopener noreferrer"
          color="blue.500"
          textDecoration="underline"
          _hover={{ color: 'blue.700' }}
          onClick={(e) => e.stopPropagation()}
        >
          {part.display}
        </Link>
      );
    }
    return <span key={index}>{part.content}</span>;
  });
};

const AnnouncementsCarousel = ({ announcements, isOpen, onClose }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = React.useRef(null);
  const baseUrl = API || "https://api.asquera.com/";

  const settings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    beforeChange: (current, next) => setCurrentSlide(next),
    arrows: false,
  };

  const gotoNext = () => {
    sliderRef.current?.slickNext();
  };

  const gotoPrev = () => {
    sliderRef.current?.slickPrev();
  };

  if (!announcements || announcements.length === 0) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      isCentered
      closeOnOverlayClick={true}
      motionPreset="slideInBottom"
    >
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent borderRadius="20px" overflow="hidden">
        <ModalCloseButton
          zIndex={10}
          color="white"
          bg="blackAlpha.500"
          _hover={{ bg: 'blackAlpha.700' }}
        />
        <ModalBody p={0} position="relative">
          <Box position="relative">
            <Slider ref={sliderRef} {...settings}>
              {announcements.map((announcement, index) => (
                <Box key={announcement.id} position="relative">
                  <VStack
                    spacing={4}
                    p={8}
                    minH="400px"
                    bg="white"
                    align="stretch"
                  >
                    {announcement.image && (
                      <Box
                        w="100%"
                        h="250px"
                        borderRadius="12px"
                        overflow="hidden"
                        bg="gray.100"
                      >
                        <Image
                          src={`${baseUrl}${announcement.image}`}
                          alt={announcement.title}
                          w="100%"
                          h="100%"
                          objectFit="cover"
                        />
                      </Box>
                    )}
                    <VStack align="stretch" spacing={3}>
                      <Text
                        fontSize="24px"
                        fontWeight="bold"
                        color="main_1"
                        textAlign="center"
                      >
                        {announcement.title}
                      </Text>
                      <Text
                        fontSize="16px"
                        color="gray.700"
                        textAlign="center"
                        lineHeight="1.6"
                        whiteSpace="pre-wrap"
                      >
                        {linkifyText(announcement.content)}
                      </Text>
                    </VStack>
                  </VStack>
                </Box>
              ))}
            </Slider>

            {/* Custom Navigation Arrows */}
            {announcements.length > 1 && (
              <>
                <IconButton
                  icon={<HiArrowLeft />}
                  onClick={gotoPrev}
                  position="absolute"
                  left="10px"
                  top="50%"
                  transform="translateY(-50%)"
                  zIndex={5}
                  bg="whiteAlpha.900"
                  _hover={{ bg: 'white' }}
                  boxShadow="lg"
                  borderRadius="full"
                  size="md"
                  aria-label="Previous announcement"
                  isDisabled={currentSlide === 0}
                />
                <IconButton
                  icon={<HiArrowRight />}
                  onClick={gotoNext}
                  position="absolute"
                  right="10px"
                  top="50%"
                  transform="translateY(-50%)"
                  zIndex={5}
                  bg="whiteAlpha.900"
                  _hover={{ bg: 'white' }}
                  boxShadow="lg"
                  borderRadius="full"
                  size="md"
                  aria-label="Next announcement"
                  isDisabled={currentSlide === announcements.length - 1}
                />
              </>
            )}

            {/* Slide Indicator */}
            {announcements.length > 1 && (
              <HStack
                position="absolute"
                bottom="20px"
                left="50%"
                transform="translateX(-50%)"
                spacing={2}
                zIndex={5}
              >
                {announcements.map((_, index) => (
                  <Box
                    key={index}
                    w={currentSlide === index ? '24px' : '8px'}
                    h="8px"
                    borderRadius="full"
                    bg={currentSlide === index ? 'main_1' : 'gray.300'}
                    transition="all 0.3s"
                  />
                ))}
              </HStack>
            )}
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AnnouncementsCarousel;

